import React, { FunctionComponent, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Toolbar,
  Tooltip,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { useSetRecoilState } from "recoil";

import { onboardingTourRequestAtom } from "recoils";

import { ConversionPathIcon, EsgLogout } from "assets";

import { useSignOut } from "hooks";
import { getLoggedUserData } from "utils";

import { COLORS } from "components/consts";
import { Notifications } from "./components";

import { ManageUsersDrawer } from "../manage-users-drawer";
import { EsgLocationTitle, H5BoldInter } from "../typography";

export const TopBar: FunctionComponent = () => {
  const onSignOut = useSignOut();
  const loggedUserData = getLoggedUserData();

  const [isMangeUsersDrawerOpen, setIsMangeUsersDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  const handlerFeedback = () => {
    const subject = encodeURIComponent("Submit Feedback");
    const body = encodeURIComponent(`Please provide your feedback here:`);
    const mailtoLink = `mailto:support@esgflo.com?subject=${subject}&body=${body}`;
    window.location.href = mailtoLink;
  };

  const currentLocation = pathname.split("/").slice(1);

  const locations: any = {
    approvals: "Approvals",
    recDetail: "Utility Provider Renewable Energy Rate Details",
    "audit-page": "Audit Logs",
    dashboard: "Dashboard",
    documents: "Documents",
    files: "Files",
    folders: "Folders",
    initiatives: "Initiatives Management",
    "initiatives-modeling": "Initiatives Modeling",
    jobs: "Jobs",
    missing: "Check Missing",
    rec: "Utility Provider Renewable Energy Rate Details",
    "renewable-energy-purchases-upload": "Renewable Energy Purchase Details",
    scorecard: "Scorecard",
    sites: "Site Management",
    "site-details": "Site Details",
    "sites-emissions": "Emissions",
    summary: "Bill Entries",
    targets: "Targets - Setup",
    "utility-specific-emissions-factors": "Utility Specific Emission Factors",
    "utility-specific-emissions-factors-details":
      "Utility Specific Emission Factors Details",
  };

  const title: any = {
    approvals: "Approvals",
    "audit-page": "Audit Logs",
    dashboard: "Dashboard",
    documents: "Documents - Upload",
    files: "Resources - Files",
    folders: "Folders",
    initiatives: "Initiatives Management",
    "initiatives-modeling": "Initiatives Modeling",
    jobs: "Jobs",
    missing: "Documents - Check Missing",
    references: "Resources - References",
    scorecard: "Scorecard",
    settings: "Preferences",
    sites: "Site Management",
    "sites-emissions": "Emissions",
    summary: "Documents - Bill Entries",
    targets: "Targets - Setup",
  };

  const requestTour = useSetRecoilState(onboardingTourRequestAtom);
  const isOnDocumentsPage =
    currentLocation[0] === "documents" && currentLocation.length === 1;

  const isModuleAdmin = loggedUserData?.roles?.includes("MODULE_ADMIN");
  const isRecOrRecDetail =
    currentLocation.includes("rec") || currentLocation.includes("recDetail");

  return (
    <>
      <AppBar elevation={0} color="inherit" position="static">
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Stack alignItems="center" direction="row" spacing={2}>
            <EsgLocationTitle>Scopes 1 & 2</EsgLocationTitle>
            {currentLocation[0] === "references" &&
              currentLocation.length === 1 && (
                <>
                  <EsgLocationTitle>/</EsgLocationTitle>
                  <EsgLocationTitle>Resources</EsgLocationTitle>
                  <EsgLocationTitle>/</EsgLocationTitle>
                  <EsgLocationTitle>References</EsgLocationTitle>
                </>
              )}
            {currentLocation[0] === "references" &&
              currentLocation.length > 1 && (
                <>
                  <EsgLocationTitle>/</EsgLocationTitle>
                  <EsgLocationTitle>Resources</EsgLocationTitle>
                </>
              )}
            {currentLocation
              .filter((item) => item !== "references")
              .map((url) => (
                <React.Fragment key={url}>
                  {locations[`${url}`] ? (
                    <>
                      <EsgLocationTitle>/</EsgLocationTitle>
                      <EsgLocationTitle>{locations[`${url}`]}</EsgLocationTitle>
                    </>
                  ) : isNaN(Number(url)) ? ( // eslint-disable-line
                    <>
                      <EsgLocationTitle>/</EsgLocationTitle>
                      <EsgLocationTitle>
                        {url.replaceAll("%20", " ")}
                      </EsgLocationTitle>
                    </>
                  ) : (
                    ""
                  )}
                </React.Fragment>
              ))}
            {currentLocation[0] === "documents" &&
              currentLocation.length === 1 && (
                <>
                  <EsgLocationTitle>/</EsgLocationTitle>
                  <EsgLocationTitle>Upload</EsgLocationTitle>
                </>
              )}
            {isRecOrRecDetail && currentLocation.length > 1 && (
              <EsgLocationTitle>{currentLocation[1] ?? ""}</EsgLocationTitle>
            )}
          </Stack>

          <Stack alignItems="center" direction="row" spacing={2}>
            <Notifications />
            <Button sx={{ px: 6 }} onClick={handlerFeedback} variant="outlined">
              Help
            </Button>
            <Button
              sx={{ px: 5 }}
              onClick={() => setIsMangeUsersDrawerOpen(true)}
              variant="outlined"
              disabled={!isModuleAdmin}
            >
              Manage users
            </Button>

            <Button
              sx={{ px: 4 }}
              startIcon={<EsgLogout width={20} height={20} />}
              onClick={onSignOut}
              variant="contained"
            >
              Sign out
            </Button>
          </Stack>
        </Toolbar>
        <Stack
          sx={{ px: 3, mt: 1 }}
          direction="row"
          alignItems="center"
          spacing={2}
        >
          <H5BoldInter>
            {currentLocation.includes("site-details")
              ? "Site Details"
              : isRecOrRecDetail
              ? "Utility Provider Renewable Energy Rate Details"
              : title[`${currentLocation.pop() as string}`]}
          </H5BoldInter>
          {isOnDocumentsPage && (
            <Box tour-step="24">
              <Tooltip title="Start Page Tour">
                <IconButton
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    backgroundColor: `${COLORS.Romance}20`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: "2px",
                  }}
                  size="small"
                  onClick={() => requestTour(true)}
                >
                  <ConversionPathIcon fill={COLORS.Romance} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Stack>
        <Divider sx={{ my: 1, borderColor: "#d9d9d9" }} />
      </AppBar>

      {isMangeUsersDrawerOpen && (
        <ManageUsersDrawer
          isOpen={isMangeUsersDrawerOpen}
          onClose={() => setIsMangeUsersDrawerOpen(false)}
        />
      )}
    </>
  );
};
